<template>
  <DeepDialog
    v-model="showDialog"
    fullscreen-on-mobile
    :title="
      i18n.t(
        isEnvelope ? 'titles.withdraw_documents' : 'titles.withdraw_document'
      )
    "
    width="600"
    @keydown.esc="showDialog = false"
  >
    <template #content>
      <v-alert
        v-if="unavailableDocuments"
        class="mb-6 w-100 text-body-small"
        data-test-id="deep-sign-alert"
        type="warning"
        variant="tonal"
      >
        {{
          i18n.t('texts.cant_withdraw_policy', {
            availableDocs: _documents.length - unavailableDocuments,
            total: _documents.length
          })
        }}
      </v-alert>
      <div class="pa-0 text-black mb-4">
        {{
          i18n.t(
            isEnvelope ? 'texts.withdraw_documents' : 'texts.withdraw_document'
          )
        }}
      </div>
      <div class="text-body-medium text-medium-emphasis mb-1">
        {{ i18n.t('titles.withdraw_message') }}
        ({{ i18n.t('captions.optional') }})
      </div>
      <v-textarea
        v-model="message"
        class="withdraw-message"
        data-test-id="withdraw-message-dialog"
        :label="i18n.t('labels.message')"
        no-resize
        :placeholder="i18n.t('placeholders.withdraw')"
        variant="outlined"
      />
    </template>
    <template #actions>
      <VBtnSecondary
        data-test-id="close-dialog-withdraw"
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="() => (showDialog = false)"
      />
      <VBtnPrimary
        data-test-id="withdraw-document"
        :disabled="unavailableDocuments === _documents.length"
        :loading="withdrawDocumentPending"
        :text="i18n.t('buttons.withdraw')"
        @click="withdraw"
      />
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import { SendMail } from '@/types/enums/SendMail';
import { useMessageStore } from '@/stores/message.ts';
import { computed, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useEmitter } from '@/composables/useEmitter';
import { useSignStore } from '@/stores/sign.ts';
import { DeepDialog } from '@deepcloud/deep-ui-lib';
import { useApi } from '@/composables/useApi.ts';

const emitter = useEmitter();

emitter.$on('open-document-withdraw-dialog', openDialog);

onUnmounted(() => {
  emitter.$off('open-document-withdraw-dialog', openDialog);
});

const { deepSignAPI } = useApi();
const showDialog = ref(false);
const message = ref();
const withdrawDocumentPending = ref(false);
const router = useRouter();
const route = useRoute();
const messageStore = useMessageStore();
const i18n = useI18n();
const documentID = ref();
const signStore = useSignStore();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _documents = ref<any[]>([]);
const emit = defineEmits(['done']);

const unavailableDocuments = computed(() => {
  return _documents.value.filter((doc) => doc.policy && !doc.policy.canWithdraw)
    .length;
});

const isEnvelope = computed(() =>
  _documents.value.some((doc) => (doc.files?.length ?? 0) > 1)
);

function openDialog(documents) {
  if (documents) _documents.value = documents;

  //If on DocumentView
  if (signStore.document && signStore.document.documentId) {
    _documents.value = [
      {
        ...signStore.document,
        policy: signStore.document.documentPolicy
      }
    ];
  }
  showDialog.value = true;
}

async function withdraw() {
  withdrawDocumentPending.value = true;
  let hasErrors = false;

  const documentsToWithdraw = _documents.value.filter((doc) => {
    return doc.policy.canWithdraw;
  });

  const withdrawPromises = documentsToWithdraw.map((document) => {
    const payload = {
      message: message.value,
      sendEmail: SendMail.OTHERS
    };

    //TODO @daniel.henkiel missing api definition -  payload is missing for PUT see WithdrawDocumentRequest
    const response = deepSignAPI.value.deepSignHttpClient.client.put(
      `/v1/documents/${document.documentId}/withdraw`,
      payload
    );
    return response;
  });

  try {
    const results = await Promise.allSettled(withdrawPromises);

    hasErrors = results.some((result) => result.status === 'rejected');

    if (!hasErrors) {
      if (route.name === 'dashboard') {
        emit('done');
      } else {
        if (route.name === 'p-document-view') {
          router.push({
            name: 'p-document-view',
            params: { document: _documents.value[0].documentId }
          });
        } else {
          router.push(`/document/${_documents.value[0].documentId}/details`);
        }
        emitter.$emit('update-sign-status');
      }
      if (_documents.value.length > 1) {
        messageStore.showMessage({
          text: i18n.t('snackbars.document_withdrawn_multiple', {
            number: _documents.value.length - unavailableDocuments.value
          }),
          color: 'success',
          icon: 'fa fa-circle-check'
        });
      } else {
        messageStore.showMessage({
          key: isEnvelope.value
            ? 'snackbars.document_withdrawn_envelope'
            : 'snackbars.document_withdrawn',
          color: 'success',
          icon: 'fa fa-circle-check'
        });
      }
    } else {
      messageStore.showMessage({
        key: 'errors.api.withdraw_document',
        color: 'error',
        icon: 'fa fa-circle-exclamation'
      });
    }
  } catch (error) {
    hasErrors = true;
    messageStore.showMessage({
      key: 'errors.api.withdraw_document',
      color: 'error',
      icon: 'fa fa-circle-exclamation'
    });
  } finally {
    withdrawDocumentPending.value = false;
    showDialog.value = false;
    message.value = undefined;
    documentID.value = undefined;
  }
}
</script>

<style scoped lang="scss">
.withdraw-message :deep(.v-input__details) {
  visibility: none;
}
</style>
